/**
 * Date / coverage helpers for insurance display.
 *
 * Why: `new Date("2026-03-15").toLocaleDateString()` parses the
 * string as **UTC midnight** and then converts to local time,
 * which silently shifts the visible date by one day in any
 * negative-UTC-offset timezone (every US time zone).  For
 * coverage dates this means a member's effective date 3/15 reads
 * as 3/14 on screen, and a termination date 3/31 reads as 3/30 —
 * directly causing claims to be billed (or denied) for days the
 * patient was actually covered.
 *
 * `fmtLocalDate` defends against that: a YYYY-MM-DD literal is
 * treated as a calendar day in the user's timezone, not as an
 * instant on the UTC timeline.  Other (already-zoned) values
 * fall through to the standard parser.
 */

/**
 * Render a coverage date as a local-tz short date.
 * Accepts:
 *   - "YYYY-MM-DD"  (bare calendar day — defended against UTC shift)
 *   - "YYYYMMDD"    (raw EDI / X12 DTP)
 *   - "YYYY-MM-DDTHH:mm…" (ISO with time / zone — fall through to standard parser)
 *   - Date instances
 * Optional `options` are passed straight to Intl.DateTimeFormat /
 * Date.prototype.toLocaleDateString so callers can pick a format
 * (e.g. `{ month: 'short', day: 'numeric', year: 'numeric' }`).
 * Returns "" when input is falsy.
 *
 * June 2026 — expanded from insurance-only to a general-purpose date
 * helper so callers across App.js can stop reinventing the same
 * UTC-defense logic.
 */
export function fmtLocalDate(value, options = undefined) {
  if (!value) return '';
  // Pure date — parse as local-tz calendar day to avoid the UTC shift.
  if (typeof value === 'string') {
    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    const ediMatch = /^(\d{4})(\d{2})(\d{2})$/.exec(value);
    const m = isoMatch || ediMatch;
    if (m) {
      const dt = new Date(+m[1], +m[2] - 1, +m[3]);
      return options ? dt.toLocaleDateString(undefined, options) : dt.toLocaleDateString();
    }
  }
  try {
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return String(value);
    return options ? dt.toLocaleDateString(undefined, options) : dt.toLocaleDateString();
  } catch {
    return String(value);
  }
}

/**
 * Best-effort "is this coverage currently active?" check.
 * Today comes from the user's local clock (start-of-day).
 *
 *   start_date <= today <= end_date  → active
 *   today > end_date                 → EXPIRED (the dangerous case)
 *   today < start_date               → not-yet-effective
 *
 * Missing end_date is treated as open-ended (active).
 */
export function getInsuranceCoverageState(insurance) {
  if (!insurance) return 'unknown';
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const parseLocal = (s) => {
    if (!s) return null;
    if (typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const [y, m, d] = s.split('-').map(Number);
      return new Date(y, m - 1, d);
    }
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const start = parseLocal(insurance.start_date);
  const end = parseLocal(insurance.end_date);

  if (end && today.getTime() > end.getTime()) return 'expired';
  if (start && today.getTime() < start.getTime()) return 'future';
  return 'active';
}

/** Just the boolean variant — handier in JSX. */
export function isInsuranceExpired(insurance) {
  return getInsuranceCoverageState(insurance) === 'expired';
}

/** True when an end_date is within `days` of today (or already past). */
export function isInsuranceEndingSoon(insurance, days = 30) {
  if (!insurance?.end_date) return false;
  const state = getInsuranceCoverageState(insurance);
  if (state === 'expired') return false; // expired is its own state
  const end =
    typeof insurance.end_date === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(insurance.end_date)
      ? (() => {
          const [y, m, d] = insurance.end_date.split('-').map(Number);
          return new Date(y, m - 1, d);
        })()
      : new Date(insurance.end_date);
  if (Number.isNaN(end.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const ms = end.getTime() - today.getTime();
  return ms >= 0 && ms <= days * 24 * 60 * 60 * 1000;
}
